import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { YMark } from "@/components/Brand";

/**
 * Legal — 1:1 web port of iOS `src/app/(auth)/legal.tsx`.
 *
 * Presented as a modal-style page with a header (Y mark left + X close
 * right), a 3-tab pill switcher (Terms / Privacy / Guidelines), and a
 * scrollable body with an eyebrow, title, intro, and titled sections.
 *
 * Copy is the same 1:1 as iOS.  The tab switcher updates the `?variant`
 * URL param in place, matching iOS `router.setParams`.
 */

const SECTIONS = {
  terms: {
    title: "Terms of Service",
    intro:
      "By using BrontoY, you agree to the following terms. This page reflects general expectations; the final version will be published before public release.",
    items: [
      {
        h: "1. Your account",
        p: "You are responsible for maintaining the security of your account and the actions taken from it. You must be at least 13 years old to use BrontoY.",
      },
      {
        h: "2. Content ownership",
        p: "You retain rights to the content you post. You grant BrontoY a licence to host, display and distribute your content within the service.",
      },
      {
        h: "3. Community standards",
        p: "You agree to follow the Community Guidelines. Content that violates them may be removed, and accounts may be suspended.",
      },
      {
        h: "4. Termination",
        p: "You may deactivate your account at any time. We may suspend or terminate accounts that violate these terms or the Guidelines.",
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro:
      "BrontoY is designed with privacy in mind. This is a summary of how we handle your data. A full policy will be published before public release.",
    items: [
      {
        h: "Information we collect",
        p: "Account details (email, password hash), profile fields you fill in, content you post, and basic operational data required to run the service.",
      },
      {
        h: "Different View feedback is private",
        p: "Feedback you send via Different View is delivered privately to the post author only. It never appears on your profile, in public feeds, in search or in notifications visible to others.",
      },
      {
        h: "Age and email",
        p: "Date of birth and email are never displayed on your public profile.",
      },
      {
        h: "Your rights",
        p: "You can edit, download or delete your profile information. Contact support to request full data export or account deletion.",
      },
    ],
  },
  guidelines: {
    title: "Community Guidelines",
    intro:
      "BrontoY is for real expression, discovery and meaningful conversation. To keep it that way, we ask you to follow a few principles.",
    items: [
      { h: "Be civil", p: "Disagreement is welcome. Personal attacks, harassment and threats are not." },
      { h: "Be honest", p: "Do not impersonate others or misrepresent your identity." },
      {
        h: "Different View is a gift, not a weapon",
        p: "Use it to offer specific, constructive perspective. 50 words. Private. Kind.",
      },
      {
        h: "No unlawful content",
        p: "Illegal content, exploitation of minors and non-consensual intimate imagery are strictly prohibited and reported where required.",
      },
      { h: "Report, don't retaliate", p: "See something wrong? Use the report tools rather than escalating publicly." },
    ],
  },
};

const TABS = [
  { key: "terms", label: "Terms" },
  { key: "privacy", label: "Privacy" },
  { key: "guidelines", label: "Guidelines" },
];

export default function Legal() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const raw = params.get("variant");
  const variant = ["terms", "privacy", "guidelines"].includes(raw) ? raw : "terms";
  const s = SECTIONS[variant];

  const close = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setVariant = (key) => {
    const next = new URLSearchParams(params);
    next.set("variant", key);
    setParams(next, { replace: true });
  };

  return (
    <div
      className="min-h-[100dvh] w-full bg-background text-foreground flex flex-col"
      data-testid="legal-screen"
    >
      {/* Header (52px) — YMark left, X close right, hairline bottom border */}
      <div
        className="h-[52px] px-5 flex items-center justify-between border-b border-border shrink-0"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <YMark height={26} />
        <button
          type="button"
          onClick={close}
          className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center hover:opacity-70 active:opacity-60 transition-opacity"
          aria-label="Close"
          data-testid="legal-close"
        >
          <X size={18} strokeWidth={2} className="text-foreground" />
        </button>
      </div>

      {/* Variant switcher — 3 pills */}
      <div className="flex gap-2.5 px-6 py-4 shrink-0">
        {TABS.map((t) => {
          const active = t.key === variant;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setVariant(t.key)}
              className={`px-[18px] py-[9px] rounded-full transition-all active:opacity-80 ${
                active ? "text-white" : "text-muted-foreground hover:opacity-90"
              }`}
              style={{
                backgroundColor: active ? "#7638FA" : "hsl(var(--secondary))",
              }}
              data-testid={`legal-${t.key}-tab`}
            >
              <span className="text-[13px] font-bold">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[560px] px-6 pt-2 pb-10">
          <p
            className="text-muted-foreground font-bold mt-2"
            style={{ fontSize: 11, letterSpacing: "1.8px" }}
            data-testid="legal-eyebrow"
          >
            BRONTOY — A BRONTO INTELLIGENCE COMPANY
          </p>
          <h1
            className="text-foreground font-jakarta font-extrabold mt-3"
            style={{ fontSize: 34, letterSpacing: "-1px", lineHeight: 1.1 }}
            data-testid={`legal-title-${variant}`}
          >
            {s.title}
          </h1>
          <p
            className="text-muted-foreground mt-3.5"
            style={{ fontSize: 16, lineHeight: "24px" }}
          >
            {s.intro}
          </p>

          <div className="flex flex-col gap-6 mt-6">
            {s.items.map((it, i) => (
              <div key={i}>
                <h2
                  className="text-foreground font-bold mb-1.5"
                  style={{ fontSize: 20, letterSpacing: "-0.4px" }}
                >
                  {it.h}
                </h2>
                <p
                  className="text-muted-foreground"
                  style={{ fontSize: 15, lineHeight: "22px" }}
                >
                  {it.p}
                </p>
              </div>
            ))}
          </div>

          <p
            className="text-muted-foreground mt-9"
            style={{ fontSize: 12 }}
          >
            Last reviewed: February 2026 · Preview version.
          </p>
        </div>
      </div>
    </div>
  );
}
